import { yupResolver } from '@hookform/resolvers/yup'
import { Typography } from '@mui/material'
import { Box, Stack } from '@mui/system'
import { FeatureFlagIdRequestSchema } from 'c1gen/features'
import { C1Button } from 'c1ui'
import { c1FormToYupSchema, RecordStringAny } from 'components/core/dynamic-form/c1FormToYupSchema'
import { RenderField } from 'components/core/dynamic-form/fields/RenderField'
import { useFeatureChecker } from 'components/no-ui/capability'
import { formToProtoStruct, getDefaultValues } from 'components/pages/entitlement/request-schema/fieldConversion'
import { canActOn } from 'components/pages/task/state'
import { useAlert } from 'hooks/useAlert'
import { TaskValue } from 'lib/actions/task'
import { Form } from 'pbts/c1/api/form/v1/form'
import { TaskActionType } from 'pbts/c1/api/task/v1/task'
import { Struct } from 'pbts/google/protobuf/struct'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useForm, Controller, ControllerRenderProps } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { useTicketActionTracker } from 'redux_toolkit/lib/TicketActionTracker'
import { RenderPbStructRequestData, RenderInformationSubmitted } from './TaskRequestedData'

type TaskStepRequiresRequestDataProps = {
    task: TaskValue
}

export const TaskStepRequiresRequestData: FC<TaskStepRequiresRequestDataProps> =
    ({ task }) => {
        const hasRequestSchemaFeature = useFeatureChecker(FeatureFlagIdRequestSchema)
        const currentStep = task.policy?.current
        const form = currentStep?.instance?.oneofKind === 'form' ? currentStep.instance.form?.form : undefined
        const fields = useMemo(() => form?.fields || [], [form])
        const data = (currentStep?.instance?.oneofKind === 'form' ? currentStep.instance.form?.data || { fields: {} } : { fields: {} }) as Struct
        const intl = useIntl()
        const [hasSubmittedData, setHasSubmittedData] = useState(false)
        const ticketActionTracker = useTicketActionTracker()
        const toast = useAlert()
        const canUpdateRequestData = canActOn(task, TaskActionType.UPDATE_REQUEST_DATA)

        const {
            control,
            formState: { errors, isValid },
            reset,
            handleSubmit,
        } = useForm<RecordStringAny>({
            resolver: yupResolver(c1FormToYupSchema(Form.create({ fields }), intl)),
            defaultValues: getDefaultValues(data, fields),
            mode: 'onTouched',
        })

        useEffect(() => {
            return () => {
                reset()
                setHasSubmittedData(false)
            }
        }, [reset, setHasSubmittedData])

        const onSubmit = useCallback(async (submittedFields: RecordStringAny): Promise<void> => {
            if (isValid) {
                try {
                    const convertedFields = formToProtoStruct(submittedFields, fields)
                    if (convertedFields) {
                        await ticketActionTracker?.updateRequestData(task, convertedFields)
                    }
                    setHasSubmittedData(true)
                } catch (error) {
                    toast(intl.formatMessage({
                        id: 'task_update_request_data_error',
                        defaultMessage: 'Error updating request data',
                    }), 'error')
                }
            }
        }, [isValid, toast, fields, intl, task, ticketActionTracker])

        const SaveButton = useMemo(() => {
            return <C1Button
                aria-label="Submit request data"
                variant="contained"
                type="submit"
                onClick={handleSubmit(onSubmit)}
                disabled={!isValid}
            >
                <FormattedMessage id="awaiting_info_submit" defaultMessage="Submit" />
            </C1Button>
        }, [handleSubmit, onSubmit, isValid])

        const RenderActionButtons = useMemo(() => {
            if (!canUpdateRequestData) {
                return null
            }

            if (hasSubmittedData) {
                return <RenderInformationSubmitted savedMessageIsActive={hasSubmittedData} />
            }

            if (canUpdateRequestData) {
                return SaveButton
            }

            return null
        }, [canUpdateRequestData, hasSubmittedData, SaveButton])

        if (!currentStep || currentStep?.instance?.oneofKind !== 'form' || !hasRequestSchemaFeature) {
            return null
        }

        if (!fields || !data || !form) {
            return null
        }

        return <Stack gap={0}>
            <Stack direction="row" justifyContent="space-between" my={1}>
                <Typography variant="h5">
                    <FormattedMessage id="awaiting_info_edit_request_data_title" defaultMessage="Requested data" />
                </Typography>
                {RenderActionButtons}
            </Stack>
            {!canUpdateRequestData ? (
                <RenderPbStructRequestData data={data} fields={fields} />
            ) : (
                <form>
                    <Stack spacing={2}>
                        {fields.map((field) => (
                            <Box key={field.name}>
                                <Controller
                                    name={field.name}
                                    control={control}
                                    render={({ field: cField }): React.ReactElement => (
                                        <RenderField
                                            field={field}
                                            cField={cField as unknown as ControllerRenderProps}
                                            errors={errors}
                                            disabled={hasSubmittedData}
                                        />
                                    )}
                                />
                                {errors[field.name] && (
                                    <Box ml={2} mt={1}>
                                        <Typography color="error">
                                            {errors[field.name]?.message as string}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Stack>
                </form>
            )}
        </Stack>
    }
